/**
 * PAI's nested routes for standalone tools, mounted at
 * `/:courseId/tools/*` via the `ToolRoutes` override (see `AppOverrides` in
 * `@cip/webapp/contexts/AppOverridesContext`).
 *
 * `ToolRoutesSlot` (webapp) already requires the caller to be signed in
 * before any of these routes render. Each route additionally declares its
 * own minimum course role via `RequireMinimumRole` — tools aren't forced to
 * share one role requirement, since a future tool might need e.g. `sl`
 * instead of `student`.
 *
 * Tools live in webapp's `demos/<tool-name>/` (not under PAI's own `src/`)
 * so they can also be rendered directly as slide elements (see
 * `@cip/webapp`'s `slides/shared/componentRegistry.tsx`) without a circular
 * dependency between the two packages. PAI-only pages that are never slide
 * elements (e.g. the mascot gallery, the roadmap prototypes) import from PAI's own `src/` instead.
 * Add a new tool by adding one entry to
 * {@link PAI_TOOLS} — no other CIP or PAI source file needs to change.
 */
import {
  Suspense,
  lazy,
  type ComponentType,
  type LazyExoticComponent,
} from 'react';
import {Route, Routes} from 'react-router-dom';
import {RequireMinimumRole} from 'routes/RequireMinimumRole';
import {Role} from 'types/role';
import {Loading} from 'components/loading/Loading';

/** One entry in PAI's tool catalog. */
interface PaiTool {
  /**
   * Sub-path under `/:courseId/tools/`. Matches the tool's folder name in
   * webapp's `demos/` (or in PAI's `src/`, for PAI-only pages).
   */
  path: string;
  /** The tool's app component, code-split into its own chunk. */
  component: LazyExoticComponent<ComponentType>;
  /** Lowest course role that may open the tool. Defaults to student. */
  minimumRole?: Role;
}

/**
 * PAI's tool catalog.
 *
 * Every entry is loaded with `lazy` deliberately. These are independent
 * single-page apps and only one is ever mounted, but importing them statically
 * pulled all of them into PAI's entry chunk — rendering that monolith cost the
 * build ~1.3 GB of peak memory over webapp's and OOM-killed the ci-build
 * runner. webapp already lazy-loads these same modules through
 * `slides/shared/componentRegistry.tsx`; a static import here also defeated
 * that split, which rollup reported as "dynamic import will not move module
 * into another chunk". See
 * https://github.com/CodeinPlace/cip/issues/3672.
 */
const PAI_TOOLS: readonly PaiTool[] = [
  {
    path: 'llm-demo',
    component: lazy(() =>
      import('demos/llm-demo/LlmDemoApp').then(m => ({default: m.LlmDemoApp})),
    ),
  },
  {
    path: 'llm-animation-demo',
    component: lazy(() =>
      import('demos/llm-animation-demo/LlmAnimationApp').then(m => ({
        default: m.LlmAnimationApp,
      })),
    ),
  },
  {
    path: 'llm-internals-demo',
    component: lazy(() =>
      import('demos/llm-internals-demo/LlmInternalsDemoApp').then(m => ({
        default: m.LlmInternalsDemoApp,
      })),
    ),
  },
  {
    path: 'dice-demo',
    component: lazy(() =>
      import('demos/dice-demo/DiceDemoApp').then(m => ({
        default: m.DiceDemoApp,
      })),
    ),
  },
  {
    path: 'ucb-demo',
    component: lazy(() =>
      import('demos/ucb-demo/UcbDemoApp').then(m => ({default: m.UcbDemoApp})),
    ),
  },
  {
    path: 'weather-demo',
    component: lazy(() =>
      import('demos/weather-demo/WeatherDemoApp').then(m => ({
        default: m.WeatherDemoApp,
      })),
    ),
  },
  {
    path: 'weather-condition-demo',
    component: lazy(() =>
      import('demos/weather-condition-demo/WeatherConditionDemoApp').then(
        m => ({
          default: m.WeatherConditionDemoApp,
        }),
      ),
    ),
  },
  {
    path: 'weather-probability-demo',
    component: lazy(() =>
      import('demos/weather-probability-demo/WeatherProbabilityDemoApp').then(
        m => ({
          default: m.WeatherProbabilityDemoApp,
        }),
      ),
    ),
  },
  {
    path: 'letters-demo',
    component: lazy(() =>
      import('demos/letters-demo/LettersDemoApp').then(m => ({
        default: m.LettersDemoApp,
      })),
    ),
  },
  {
    path: 'freethrow-demo',
    component: lazy(() =>
      import('demos/freethrow-demo/FreeThrowDemoApp').then(m => ({
        default: m.FreeThrowDemoApp,
      })),
    ),
  },
  {
    path: 'netflix-demo',
    component: lazy(() =>
      import('demos/netflix-demo/NetflixDemoApp').then(m => ({
        default: m.NetflixDemoApp,
      })),
    ),
  },
  {
    path: 'code-count-demo',
    component: lazy(() =>
      import('demos/code-count-demo/CodeCountDemoApp').then(m => ({
        default: m.CodeCountDemoApp,
      })),
    ),
  },
  {
    path: 'sample-size-demo',
    component: lazy(() =>
      import('demos/sample-size-demo/SampleSizeDemoApp').then(m => ({
        default: m.SampleSizeDemoApp,
      })),
    ),
  },
  {
    path: 'word-probability-demo',
    component: lazy(() =>
      import('demos/word-probability-demo/WordProbabilityDemoApp').then(m => ({
        default: m.WordProbabilityDemoApp,
      })),
    ),
  },
  {
    path: 'off-by-one-demo',
    component: lazy(() =>
      import('demos/off-by-one-demo/OffByOneDemoApp').then(m => ({
        default: m.OffByOneDemoApp,
      })),
    ),
  },
  {
    path: 'bayes-demo',
    component: lazy(() =>
      import('demos/bayes-demo/BayesDemoApp').then(m => ({
        default: m.BayesDemoApp,
      })),
    ),
  },
  {
    path: 'eikosogram-demo',
    component: lazy(() =>
      import('demos/eikosogram-demo/EikosogramDemoApp').then(m => ({
        default: m.EikosogramDemoApp,
      })),
    ),
  },
  {
    path: 'monty-hall-demo',
    component: lazy(() =>
      import('demos/monty-hall-demo/MontyHallDemoApp').then(m => ({
        default: m.MontyHallDemoApp,
      })),
    ),
  },
  {
    path: 'tree-demo',
    component: lazy(() =>
      import('demos/tree-demo/TreeDemoApp').then(m => ({
        default: m.TreeDemoApp,
      })),
    ),
  },
  {
    path: 'simpson-demo',
    component: lazy(() =>
      import('demos/simpson-demo/SimpsonDemoApp').then(m => ({
        default: m.SimpsonDemoApp,
      })),
    ),
  },
  {
    path: 'venn-demo',
    component: lazy(() =>
      import('demos/venn-demo/VennDemoApp').then(m => ({
        default: m.VennDemoApp,
      })),
    ),
  },
  {
    path: 'galton-demo',
    component: lazy(() =>
      import('demos/galton-demo/GaltonDemoApp').then(m => ({
        default: m.GaltonDemoApp,
      })),
    ),
  },
  {
    path: 'monte-carlo-demo',
    component: lazy(() =>
      import('demos/monte-carlo-demo/MonteCarloDemoApp').then(m => ({
        default: m.MonteCarloDemoApp,
      })),
    ),
  },
  {
    path: 'clt-demo',
    component: lazy(() =>
      import('demos/clt-demo/CltDemoApp').then(m => ({default: m.CltDemoApp})),
    ),
  },
  {
    path: 'buffon-demo',
    component: lazy(() =>
      import('demos/buffon-demo/BuffonDemoApp').then(m => ({
        default: m.BuffonDemoApp,
      })),
    ),
  },
  {
    path: 'birthday-demo',
    component: lazy(() =>
      import('demos/birthday-demo/BirthdayDemoApp').then(m => ({
        default: m.BirthdayDemoApp,
      })),
    ),
  },
  {
    path: 'prisoners-demo',
    component: lazy(() =>
      import('demos/prisoners-demo/PrisonersDemoApp').then(m => ({
        default: m.PrisonersDemoApp,
      })),
    ),
  },
  {
    path: 'card-counting-demo',
    component: lazy(() =>
      import('demos/card-counting-demo/CardCountingDemoApp').then(m => ({
        default: m.CardCountingDemoApp,
      })),
    ),
  },
  {
    path: 'fourier-demo',
    component: lazy(() =>
      import('demos/fourier-demo/FourierDemoApp').then(m => ({
        default: m.FourierDemoApp,
      })),
    ),
  },
  {
    path: 'chaos-pendulum-demo',
    component: lazy(() =>
      import('demos/chaos-pendulum-demo/ChaosPendulumDemoApp').then(m => ({
        default: m.ChaosPendulumDemoApp,
      })),
    ),
  },
  {
    path: 'life-demo',
    component: lazy(() =>
      import('demos/life-demo/LifeDemoApp').then(m => ({
        default: m.LifeDemoApp,
      })),
    ),
  },
  {
    path: 'sync-demo',
    component: lazy(() =>
      import('demos/sync-demo/SyncDemoApp').then(m => ({
        default: m.SyncDemoApp,
      })),
    ),
  },
  {
    path: 'times-table-demo',
    component: lazy(() =>
      import('demos/times-table-demo/TimesTableDemoApp').then(m => ({
        default: m.TimesTableDemoApp,
      })),
    ),
  },
  {
    path: 'eigen-demo',
    component: lazy(() =>
      import('demos/eigen-demo/EigenDemoApp').then(m => ({
        default: m.EigenDemoApp,
      })),
    ),
  },
  {
    path: 'soccer-demo',
    component: lazy(() =>
      import('demos/soccer-demo/SoccerDemoApp').then(m => ({
        default: m.SoccerDemoApp,
      })),
    ),
  },
  {
    path: 'belief-demo',
    component: lazy(() =>
      import('demos/belief-demo/BeliefDemoApp').then(m => ({
        default: m.BeliefDemoApp,
      })),
    ),
  },
  {
    path: 'data-dump-demo',
    component: lazy(() =>
      import('demos/data-dump-demo/DataDumpDemoApp').then(m => ({
        default: m.DataDumpDemoApp,
      })),
    ),
  },
  {
    path: 'word-detective-demo',
    component: lazy(() =>
      import('demos/word-detective-demo/WordDetectiveDemoApp').then(m => ({
        default: m.WordDetectiveDemoApp,
      })),
    ),
  },
  {
    path: 'cute-widgets',
    component: lazy(() =>
      import('./cuteWidgets/CuteWidgetsPage').then(m => ({
        default: m.CuteWidgetsPage,
      })),
    ),
  },
  {
    path: 'mascot',
    component: lazy(() =>
      import('./mascot/PaiMascotPage').then(m => ({
        default: m.PaiMascotPage,
      })),
    ),
  },
  {
    path: 'teacher-home-prototypes',
    component: lazy(() =>
      import('./teacherHome/PaiTeacherHomePrototypesPage').then(m => ({
        default: m.PaiTeacherHomePrototypesPage,
      })),
    ),
    minimumRole: Role.SECTION_LEADER,
  },
  {
    path: 'twist-versions',
    component: lazy(() =>
      import('./twistVersions/TwistVersionsPage').then(m => ({
        default: m.TwistVersionsPage,
      })),
    ),
    minimumRole: Role.SECTION_LEADER,
  },
  {
    path: 'roadmap-prototypes',
    component: lazy(() =>
      import('./roadmap/PaiRoadmapPrototypesPage').then(m => ({
        default: m.PaiRoadmapPrototypesPage,
      })),
    ),
  },
];

/**
 * Renders PAI's tool catalog at whatever sub-path under `/tools/` was hit.
 *
 * @return The tool routes, behind a suspense boundary for the lazy chunks.
 *     `ToolRoutesSlot` renders an override without one of its own.
 */
export function PaiToolRoutes() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {PAI_TOOLS.map(({path, component: Tool, minimumRole}) => (
          <Route
            key={path}
            path={path}
            element={
              <RequireMinimumRole minimumRole={minimumRole ?? Role.STUDENT}>
                <Tool />
              </RequireMinimumRole>
            }
          />
        ))}
      </Routes>
    </Suspense>
  );
}
